import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { DsModalService, DsListMenuItem, DsTreeMenuItem, Icons } from '@levelaccess/design-system';
import { BehaviorSubject, combineLatest, from, Observable, of, Subscription } from 'rxjs';
import { map, shareReplay, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { Dictionary, keyBy, sortBy } from 'lodash';
import { DigitalPropertyTypeUtility } from '@shared/utils/digital-property-type.utility';

import { Api } from '../../../../shared/constants/api';
import { $digitalProperty, $digitalPropertyType } from '../../../../shared/constants/digital-properties';
import { FunctionalArea, SecureAction } from '../../../../shared/constants/security';
import { $workspace } from '../../../../shared/constants/workspace';
import {
  IDigitalPropertiesListResponse,
  IDigitalPropertyListItem,
  IDigitalPropertyType,
} from '../../../../shared/interfaces/digital-property.interface';
import { IUserServerResponse } from '../../../../shared/interfaces/user.interface';
import { SharedCommonUtility } from '../../../../shared/utils/common.utility';
import { LinkedPropertyUtility } from '../../../../shared/utils/linked-property.utility';
import { AclSecurityAdapter } from '../acl.service';
import { BusMessageChannels, BusMessageService } from '../bus-message.service';
import { DigitalPropertyService } from '../digital-property.service';
import { ErrorHandlerService } from '../error-handler.service';
import { UserAclService } from '../user-acl.service';
import { UserPropertyService } from '../user-property.service';
import { UserService } from '../user.service';
import { INavigationContextItem, INavMenuItem, NavigationItem } from './nav-structure';
import { NavService } from './nav.service';
import { AppConfigService } from '../app-config.service';
import { IRecentUserResources } from '../../../../shared/interfaces/recent-resources.interface';
import { $recentUserResources } from '../../../../shared/constants/recent-resources';
import { RoutingService } from '../routing.service';

const RECENT_NAV_ITEMS_LIMIT: number = 3;

@Injectable({
  providedIn: 'root',
})
export class LauncherNavService implements OnDestroy {
  public navItems$: Observable<DsTreeMenuItem[]>;
  public recentNavItems$: Observable<DsListMenuItem[]>;
  public searchQuery$: BehaviorSubject<string>;
  private subscriptions: Subscription;

  constructor(
    private navService: NavService,
    private userPropertyService: UserPropertyService,
    private router: Router,
    private userService: UserService,
    private activatedRoute: ActivatedRoute,
    private errorHandlerService: ErrorHandlerService,
    private busMessageService: BusMessageService,
    private modalService: DsModalService,
    private userAclService: UserAclService,
    private digitalPropertyService: DigitalPropertyService,
    private appConfigService: AppConfigService,
    private routingService: RoutingService,
  ) {
    this.searchQuery$ = new BehaviorSubject<string>(null);
    this.subscriptions = new Subscription();
    this.navItems$ = combineLatest([this.getAvailableNavItems(), this.getActiveNavItemId(), this.searchQuery$]).pipe(
      map(([navItems, activeItem, searchQuery]: [DsTreeMenuItem[], string, string]) => {
        return this.filterNavItems(this.setActiveItem(navItems, activeItem), searchQuery);
      }),
    );
    this.recentNavItems$ = this.getRecentNavItems();
  }

  public setSearchQuery(query: string): void {
    this.searchQuery$.next(query);
  }

  public get hasAccessToLauncher(): boolean {
    return this.appConfigService.isTenantEnabled();
  }

  private filterNavItems(items: DsTreeMenuItem[], searchTerm: string): DsTreeMenuItem[] {
    if (SharedCommonUtility.isNullishOrEmpty(searchTerm?.trim())) {
      return this.sortNavItems(items);
    }

    const lowerSearchTerm: string = searchTerm?.trim().toLowerCase();
    const filteredNav: DsTreeMenuItem[] = items
      .map((item: DsTreeMenuItem) => {
        return {
          ...item,
          children: item.children ? this.filterNavItems(item.children, searchTerm) : null,
        };
      })
      .filter(
        (item: DsTreeMenuItem) =>
          item.label?.toLowerCase().includes(lowerSearchTerm) || (item.children && item.children.length > 0), // Keep parent if it has matching children
      )
      .map((item: DsTreeMenuItem) => {
        const result: DsTreeMenuItem = {
          ...item,
          children: SharedCommonUtility.isNullishOrEmpty(item.children) ? null : item.children,
        };

        if (!SharedCommonUtility.isNullishOrEmpty(item.children)) {
          result.expanded = true;
        }

        return result;
      });

    return this.sortNavItems(filteredNav);
  }

  private sortNavItems(items: DsTreeMenuItem[]): DsTreeMenuItem[] {
    if (SharedCommonUtility.isNullishOrEmpty(items)) {
      return [];
    }

    return sortBy(
      items.map((item: DsTreeMenuItem) => ({
        ...item,
        children: item.children ? this.sortNavItems(item.children) : null,
      })),
      (item: DsTreeMenuItem) => (item.label || '').toLowerCase(),
    );
  }

  private getAvailableNavItems(): Observable<DsTreeMenuItem[]> {
    const organizationNavItem$: Observable<DsTreeMenuItem> = this.navService
      .getTopLevelMenuItems$([NavigationItem.organization_dashboard])
      .pipe(
        take(1),
        map((menuItems: INavMenuItem[]) => {
          const organizationNavItem: INavMenuItem = menuItems[0];
          return {
            id: organizationNavItem.id,
            label: organizationNavItem.label,
            icon: Icons.Building,
            onClick: (): void => {
              this.routingService.requestMoveFocusOnNavigationEnd();
              this.router.navigate([organizationNavItem.routerLink]);
              this.modalService.closeAllModals();
            },
            disabled: !organizationNavItem.isAvailable,
          };
        }),
      );

    const digitalProperties$: Observable<{ items: DsTreeMenuItem[]; tenantId: string; tenantName: string }> = combineLatest([
      this.userPropertyService.getAvailableDigitalProperties(),
      this.digitalPropertyService.getDigitalPropertyTypes(),
    ]).pipe(
      map(([digitalProperties, types]: [IDigitalPropertiesListResponse, IDigitalPropertyType[]]) => {
        return {
          items: this.mapNavItems(digitalProperties.items, types),
          tenantId: digitalProperties.items[0]?.[$digitalProperty.workspace][$workspace.tenantId],
          tenantName: digitalProperties.items[0]?.[$digitalProperty.workspace][$workspace.tenant].name,
        };
      }),
      shareReplay(1),
    );

    return combineLatest([organizationNavItem$, digitalProperties$]).pipe(
      map(
        ([organizationNavItem, digitalProperties]: [
          DsTreeMenuItem,
          { items: DsTreeMenuItem[]; tenantId: string; tenantName: string },
        ]) => {
          organizationNavItem.id = digitalProperties.tenantId;
          organizationNavItem.label = digitalProperties.tenantName;
          return [{ ...organizationNavItem, children: digitalProperties.items }];
        },
      ),
    );
  }

  private flattenNavItems(items: DsTreeMenuItem[]): DsTreeMenuItem[] {
    return items.reduce((acc: DsTreeMenuItem[], item: DsTreeMenuItem) => {
      acc.push(item);
      if (SharedCommonUtility.notNullishOrEmpty(item.children)) {
        acc.push(...this.flattenNavItems(item.children));
      }
      return acc;
    }, []);
  }

  private getRecentNavItems(): Observable<DsListMenuItem[]> {
    return combineLatest([this.navItems$, this.userService.getRecentUserResources()]).pipe(
      map(([navItems, recentUserResources]: [DsTreeMenuItem[], IRecentUserResources]) => {
        if (SharedCommonUtility.isNullishOrEmpty(recentUserResources?.[$recentUserResources.digitalPropertyIds])) {
          return [];
        }

        const navItemsMap: Dictionary<DsTreeMenuItem> = keyBy(this.flattenNavItems(navItems), 'id');
        const recentNavItems: DsTreeMenuItem[] = recentUserResources[$recentUserResources.digitalPropertyIds]
          .map((digitalPropertyId: string) => navItemsMap[digitalPropertyId])
          .filter(SharedCommonUtility.notNullish);
        const recentNavItemsWithoutCurrent: DsTreeMenuItem[] = recentNavItems.slice(1, RECENT_NAV_ITEMS_LIMIT + 1);
        return recentNavItemsWithoutCurrent;
      }),
    );
  }

  private mapNavItems(digitalProperties: IDigitalPropertyListItem[], types: IDigitalPropertyType[]): DsTreeMenuItem[] {
    const workspacesMap: { [key: string]: DsTreeMenuItem } = {};

    digitalProperties.forEach((property: IDigitalPropertyListItem) => {
      const workspaceId: string = property[$digitalProperty.workspace][$workspace._id];
      if (!workspacesMap[workspaceId]) {
        workspacesMap[workspaceId] = {
          id: workspaceId,
          label: property[$digitalProperty.workspace][$workspace.name],
          icon: Icons.Briefcase,
          onClick: (): void =>
            this.navigateToWorkspace(workspaceId, this.getDigitalPropertyIdsInWorkspace(workspacesMap[workspaceId])),
          children: [],
        };
      }
      workspacesMap[workspaceId].children.push({
        id: property[$digitalProperty._id],
        label: property[$digitalProperty.name],
        icon: this.navService.getIconForDigitalPropertyTypeId(property[$digitalProperty.typeId], types),
        onClick: () =>
          this.navigateToDigitalProperty(
            workspaceId,
            property[$digitalProperty._id],
            types.find((t: IDigitalPropertyType) => t[$digitalPropertyType._id] === property[$digitalProperty.typeId]),
          ),
      });
    });

    return Object.values(workspacesMap).sort((a: DsTreeMenuItem, b: DsTreeMenuItem) => a.label.localeCompare(b.label));
  }

  private getActiveNavItemId(): Observable<string> {
    return this.navService.getCurrentContextItem$().pipe(map((item: INavigationContextItem) => item?.id || null));
  }

  private setActiveItem(navItems: DsTreeMenuItem[], activeItemId: string, level: number = 0): DsTreeMenuItem[] {
    return navItems.map((item: DsTreeMenuItem) => {
      item.active = activeItemId === item.id;

      let hasActiveChild: boolean = false;

      if (item.children) {
        item.children = this.setActiveItem(item.children, activeItemId, level + 1);
        hasActiveChild = item.children.some((child: DsTreeMenuItem) => child.active || child.expanded);
      }

      if (level === 0) {
        item.expanded = true;
      } else {
        item.expanded = item.active || hasActiveChild;
      }

      return item;
    });
  }

  private getDigitalPropertyIdsInWorkspace(workspaceNavItem: DsTreeMenuItem): string[] {
    return workspaceNavItem.children.map((c: DsTreeMenuItem) => c.id);
  }

  public navigateToWorkspace(workspaceId: string, digitalPropertyIds: string[]): void {
    this.subscriptions.add(
      combineLatest([this.navService.getTopLevelMenuItems$([NavigationItem.workspace]), this.getActiveNavItemId()])
        .pipe(
          take(1),
          switchMap(([menuItems, activeNavItemId]: [INavMenuItem[], string]) => {
            const workspacesMenuItem: INavMenuItem = menuItems[0];
            const digitalPropertyId: string = digitalPropertyIds.includes(activeNavItemId)
              ? activeNavItemId
              : digitalPropertyIds[0];

            return this.userService.switchUserToDigitalProperty(workspaceId, digitalPropertyId).pipe(
              take(1),
              switchMap(() => this.navService.activeMenuItemForWorkspaceSwitcher$().pipe(take(1))),
              switchMap((refreshedActiveMenuItem: INavMenuItem | undefined) => {
                const routePath: string =
                  SharedCommonUtility.notNullish(refreshedActiveMenuItem) &&
                  refreshedActiveMenuItem.routerLink !== `/${Api.dashboard}`
                    ? refreshedActiveMenuItem.routerLink
                    : workspacesMenuItem.routerLink;

                this.routingService.requestMoveFocusOnNavigationEnd();
                return from(
                  this.router
                    .navigate([routePath], {
                      queryParams: LinkedPropertyUtility.getLinkedPropertyQueryParam(digitalPropertyId, workspaceId),
                      relativeTo: this.activatedRoute,
                      queryParamsHandling: 'merge',
                    })
                    .catch(this.errorHandlerService.handleRoutingError.bind(this.errorHandlerService)),
                );
              }),
            );
          }),
        )
        .subscribe(() => {
          this.busMessageService.to(BusMessageChannels.userSwitchedToWorkspace).next();
          this.busMessageService.to(BusMessageChannels.userSwitchedToDigitalProperty).next();
          this.modalService.closeAllModals();
        }),
    );
  }

  private navigateToDigitalProperty(workspaceId: string, digitalPropertyId: string, type: IDigitalPropertyType | null): void {
    this.subscriptions.add(
      this.userService
        .switchUserToDigitalProperty(workspaceId, digitalPropertyId)
        .pipe(
          withLatestFrom(this.navService.activeThirdLevelMenuItem$()),
          switchMap(([_, navMenuItem]: [IUserServerResponse, INavMenuItem]): Observable<string> => {
            if (
              !SharedCommonUtility.isStringEmpty(navMenuItem?.routerLink) &&
              navMenuItem.id !== NavigationItem.websites_and_apps_portfolio &&
              navMenuItem.parent.id === NavigationItem.websites_and_apps
            ) {
              return of(navMenuItem.routerLink);
            }
            return this.userAclService.createCheckAccessForCurrentUser().pipe(
              take(1),
              map((adapter: AclSecurityAdapter): [boolean, boolean] => {
                const hasDashboardAccess: boolean = adapter
                  .useWorkspaceFromUser()
                  .useDigitalPropertyFromUser()
                  .useFunctionalActions({
                    [FunctionalArea.gr_dashboard]: [SecureAction.read],
                  })
                  .check();
                const hasMobileODTAccess: boolean =
                  SharedCommonUtility.notNullish(type) &&
                  DigitalPropertyTypeUtility.isMobile(type) &&
                  adapter
                    .useWorkspaceFromUser()
                    .useDigitalPropertyFromUser()
                    .useFunctionalActions({
                      [FunctionalArea.at_mobile_odt_scans]: [SecureAction.read],
                    })
                    .check();
                return [hasDashboardAccess, hasMobileODTAccess];
              }),
              map(([hasDashboardAccess, hasMobileODTAccess]: [boolean, boolean]): string => {
                if (hasDashboardAccess) {
                  return `/${Api.dashboard}`;
                }
                if (hasMobileODTAccess) {
                  return `/${Api.websitesAndApps}/${Api.mobile_scans}`;
                }
                return `/${Api.manual_evaluations}`;
              }),
            );
          }),
        )
        .subscribe((route: string) => {
          const extras: NavigationExtras = {
            queryParams: LinkedPropertyUtility.getLinkedPropertyQueryParam(digitalPropertyId, workspaceId),
            relativeTo: this.activatedRoute,
            queryParamsHandling: 'merge',
            skipLocationChange: false,
          };

          this.routingService.requestMoveFocusOnNavigationEnd();
          this.router
            .navigate([route], extras)
            .then(() => {
              this.busMessageService.to(BusMessageChannels.userSwitchedToDigitalProperty).next();
              this.modalService.closeAllModals();
            })
            .catch(this.errorHandlerService.handleRoutingError.bind(this.errorHandlerService));
        }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
  }
}
